import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Sun, Moon, Heart, Activity, Smile, Frown, Meh,
  CheckCircle, AlertTriangle, Clock, TrendingUp, TrendingDown,
  Calendar, Send, Loader2, RefreshCw, ChevronRight,
  User, Bell, Coffee, Bed, Wind, MessageCircle
} from "lucide-react";

const QUESTION_ICONS = {
  overnight_comfort: Moon,
  pain_level: Activity,
  sleep_quality: Bed,
  appetite: Coffee,
  breathing: Wind,
  mood: Smile,
  concerns: MessageCircle,
  caregiver_wellness: Heart
};

export default function WellnessCheckin() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [todayData, setTodayData] = useState(null);
  const [history, setHistory] = useState(null);
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [todayRes, historyRes] = await Promise.all([
        api.get("/phase73/today"),
        api.get("/phase73/history?days=7")
      ]);
      setTodayData(todayRes.data);
      setHistory(historyRes.data);
      
      // If already completed, show that
      if (todayRes.data.completed) {
        setResponses(todayRes.data.checkin?.responses || {});
      }
    } catch (error) {
      console.error("Error loading wellness data:", error);
      // Set default questions for demo
      setTodayData({
        completed: false,
        questions: [
          {
            id: "overnight_comfort",
            question: "How was your loved one's overnight comfort?",
            type: "scale",
            options: [
              { value: 1, label: "Very Uncomfortable", alert: true },
              { value: 2, label: "Uncomfortable", alert: true },
              { value: 3, label: "Fair", alert: false },
              { value: 4, label: "Comfortable", alert: false },
              { value: 5, label: "Very Comfortable", alert: false }
            ]
          },
          {
            id: "pain_level",
            question: "What is their current pain level?",
            type: "scale",
            options: Array.from({ length: 11 }, (_, i) => ({
              value: i,
              label: i === 0 ? "No Pain" : i === 10 ? "Severe" : String(i),
              alert: i >= 6
            }))
          },
          {
            id: "caregiver_wellness",
            question: "How are YOU doing today?",
            type: "scale",
            options: [
              { value: 1, label: "Struggling", alert: true },
              { value: 2, label: "Tired", alert: false },
              { value: 3, label: "Managing", alert: false },
              { value: 4, label: "Good", alert: false },
              { value: 5, label: "Great", alert: false }
            ]
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResponse = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    const questions = todayData?.questions || [];
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/phase73/submit", {
        responses,
        notes: notes || null
      });
      
      setSuccessData(res.data);
      setShowSuccess(true);
      loadData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("You've already submitted today's check-in");
      } else {
        toast.error("Failed to submit check-in");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getScoreColor = (value, max = 5, inverted = false) => {
    const ratio = value / max;
    if (inverted) {
      if (ratio <= 0.3) return "text-green-600";
      if (ratio <= 0.6) return "text-yellow-600";
      return "text-red-600";
    }
    if (ratio >= 0.7) return "text-green-600";
    if (ratio >= 0.4) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreEmoji = (value, max = 5) => {
    const ratio = value / max;
    if (ratio >= 0.7) return <Smile className="w-5 h-5 text-green-600" />;
    if (ratio >= 0.4) return <Meh className="w-5 h-5 text-yellow-600" />;
    return <Frown className="w-5 h-5 text-red-600" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="wellness-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-600 font-medium">Loading wellness check-in...</p>
        </div>
      </div>
    );
  }

  const questions = todayData?.questions || [];
  const isComplete = todayData?.completed;
  const currentQuestion = questions[currentStep];
  const progress = questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;
  const allAnswered = questions.every(q => responses[q.id] !== undefined);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white p-6" data-testid="wellness-checkin">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Sun className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Daily Wellness Check-in</h1>
          <p className="text-slate-600 mt-2">
            {isComplete 
              ? "You've completed today's check-in. Thank you!"
              : "A quick check on your loved one's comfort and your wellbeing"}
          </p>
        </div>

        <Tabs defaultValue={isComplete ? "history" : "checkin"}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="checkin" className="flex-1">
              {isComplete ? "Today's Results" : "Check-in"}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1">
              History
            </TabsTrigger>
          </TabsList>

          {/* Check-in Tab */}
          <TabsContent value="checkin">
            {isComplete ? (
              /* Completed View */
              <Card data-testid="completed-checkin">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <CardTitle>Today's Check-in Complete</CardTitle>
                  </div>
                  <CardDescription>
                    Submitted at {new Date(todayData.checkin?.submitted_at).toLocaleTimeString()}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {questions.map((q) => {
                    const IconComponent = QUESTION_ICONS[q.id] || Activity;
                    const responseValue = responses[q.id];
                    const selectedOption = q.options?.find(o => o.value === responseValue);
                    
                    return (
                      <div key={q.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <IconComponent className="w-5 h-5 text-slate-600" />
                          <span className="text-sm text-slate-700">{q.question}</span>
                        </div>
                        <Badge className={selectedOption?.alert ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                          {selectedOption?.label || responseValue}
                        </Badge>
                      </div>
                    );
                  })}
                  
                  {todayData.checkin?.notes && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Additional Notes:</p>
                      <p className="text-slate-700">{todayData.checkin.notes}</p>
                    </div>
                  )}
                  
                  {todayData.checkin?.alert_triggered && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Care Team Notified</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Based on your responses, we've alerted your care team. Someone will be in touch soon.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Check-in Form */
              <Card data-testid="checkin-form">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        Question {currentStep + 1} of {questions.length}
                      </CardTitle>
                    </div>
                    <Badge variant="outline">
                      {Math.round(progress)}% Complete
                    </Badge>
                  </div>
                  <Progress value={progress} className="mt-2" />
                </CardHeader>
                <CardContent>
                  {currentQuestion && (
                    <div className="space-y-6">
                      {/* Question */}
                      <div className="text-center py-4">
                        {(() => {
                          const IconComponent = QUESTION_ICONS[currentQuestion.id] || Activity;
                          return <IconComponent className="w-12 h-12 text-teal-600 mx-auto mb-4" />;
                        })()}
                        <h2 className="text-xl font-semibold text-slate-900">
                          {currentQuestion.question}
                        </h2>
                      </div>

                      {/* Options */}
                      {currentQuestion.type === "scale" && (
                        <div className="space-y-2">
                          {currentQuestion.options.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handleResponse(currentQuestion.id, option.value)}
                              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                                responses[currentQuestion.id] === option.value
                                  ? option.alert
                                    ? "border-red-500 bg-red-50"
                                    : "border-teal-500 bg-teal-50"
                                  : "border-slate-200 hover:border-slate-300 bg-white"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-900">{option.label}</span>
                                {responses[currentQuestion.id] === option.value && (
                                  <CheckCircle className={`w-5 h-5 ${option.alert ? "text-red-500" : "text-teal-500"}`} />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {currentQuestion.type === "choice" && (
                        <div className="grid grid-cols-2 gap-3">
                          {currentQuestion.options.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handleResponse(currentQuestion.id, option.value)}
                              className={`p-4 rounded-lg border-2 transition-all ${
                                responses[currentQuestion.id] === option.value
                                  ? option.alert
                                    ? "border-red-500 bg-red-50"
                                    : "border-teal-500 bg-teal-50"
                                  : "border-slate-200 hover:border-slate-300 bg-white"
                              }`}
                            >
                              <span className="font-medium text-slate-900">{option.label}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {currentQuestion.type === "text" && (
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder={currentQuestion.placeholder}
                          rows={4}
                          className="w-full"
                        />
                      )}

                      {/* Navigation */}
                      <div className="flex justify-between pt-4">
                        <Button
                          variant="outline"
                          onClick={handlePrev}
                          disabled={currentStep === 0}
                        >
                          Previous
                        </Button>

                        {currentStep < questions.length - 1 ? (
                          <Button
                            onClick={handleNext}
                            disabled={responses[currentQuestion.id] === undefined && currentQuestion.type !== "text"}
                          >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        ) : (
                          <Button
                            onClick={handleSubmit}
                            disabled={!allAnswered || submitting}
                          >
                            {submitting ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Submit Check-in
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            {history && (
              <div className="space-y-6">
                {/* Trends */}
                <Card data-testid="wellness-trends">
                  <CardHeader>
                    <CardTitle className="text-lg">7-Day Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-center gap-2">
                          {history.trends.avg_comfort && getScoreEmoji(history.trends.avg_comfort, 5)}
                          <span className={`text-2xl font-bold ${getScoreColor(history.trends.avg_comfort || 0, 5)}`}>
                            {history.trends.avg_comfort || "-"}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Avg Comfort</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <span className={`text-2xl font-bold ${getScoreColor(history.trends.avg_pain || 0, 10, true)}`}>
                          {history.trends.avg_pain || "-"}
                        </span>
                        <p className="text-sm text-slate-600 mt-1">Avg Pain</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-center gap-2">
                          <Heart className={`w-5 h-5 ${getScoreColor(history.trends.avg_caregiver_wellness || 0, 5)}`} />
                          <span className={`text-2xl font-bold ${getScoreColor(history.trends.avg_caregiver_wellness || 0, 5)}`}>
                            {history.trends.avg_caregiver_wellness || "-"}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Your Wellness</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <div>
                          <p className="text-xl font-bold text-green-700">{history.trends.checkins_completed}</p>
                          <p className="text-sm text-green-600">Check-ins</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                        <Bell className="w-6 h-6 text-amber-600" />
                        <div>
                          <p className="text-xl font-bold text-amber-700">{history.trends.alerts_triggered}</p>
                          <p className="text-sm text-amber-600">Alerts</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* History List */}
                <Card data-testid="checkin-history">
                  <CardHeader>
                    <CardTitle className="text-lg">Recent Check-ins</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {history.checkins?.length > 0 ? (
                      history.checkins.map((checkin) => (
                        <div 
                          key={checkin.id} 
                          className={`p-4 rounded-lg border ${
                            checkin.alert_triggered ? "border-amber-200 bg-amber-50" : "border-slate-200"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Calendar className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="font-medium text-slate-900">
                                  {new Date(checkin.date).toLocaleDateString("en-US", { 
                                    weekday: "short", 
                                    month: "short", 
                                    day: "numeric" 
                                  })}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {new Date(checkin.submitted_at).toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "2-digit"
                                  })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {checkin.responses?.overnight_comfort && (
                                <Badge variant="outline">
                                  Comfort: {checkin.responses.overnight_comfort}/5
                                </Badge>
                              )}
                              {checkin.alert_triggered && (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Alert
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-slate-500 py-4">No check-ins yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <DialogTitle className="text-center">Thank You!</DialogTitle>
            <DialogDescription className="text-center">
              {successData?.message}
            </DialogDescription>
          </DialogHeader>
          
          {successData?.alert_triggered && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg my-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Care Team Notified</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {successData.alerts_count} concern(s) have been flagged. Your care team will reach out soon.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowSuccess(false)} className="w-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
